import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'xsscool:preview-width';
const MIN_PX = 280;
const MAX_VW_FRAC = 0.7;

function loadPersisted(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clamp(px: number): number {
  const max = window.innerWidth * MAX_VW_FRAC;
  return Math.min(Math.max(px, MIN_PX), max);
}

// 6 px draggable gutter between the main pane and the preview iframe.
// Updates --preview-width on .shell, which the grid template reads. Persists
// the result so reopening the preview keeps your width.
export function PreviewSplitter() {
  const ref = useRef<HTMLDivElement | null>(null);

  // Apply persisted width on mount.
  useEffect(() => {
    const persisted = loadPersisted();
    if (persisted) {
      document.querySelector<HTMLElement>('.shell')?.style.setProperty(
        '--preview-width',
        `${clamp(persisted)}px`,
      );
    }
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const shell = document.querySelector<HTMLElement>('.shell');
    if (!shell) return;

    const onMove = (ev: PointerEvent) => {
      // Preview width = (shell right edge) − (cursor x) − (gutter width / 2).
      const rect = shell.getBoundingClientRect();
      const next = clamp(rect.right - ev.clientX);
      shell.style.setProperty('--preview-width', `${next}px`);
    };
    const onUp = (ev: PointerEvent) => {
      el.releasePointerCapture(ev.pointerId);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      const cur = shell.style.getPropertyValue('--preview-width');
      const px = parseFloat(cur);
      if (Number.isFinite(px)) {
        try {
          localStorage.setItem(STORAGE_KEY, String(Math.round(px)));
        } catch {
          /* ignore */
        }
      }
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      ref={ref}
      className="preview-splitter"
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize preview pane"
      title="Drag to resize"
    />
  );
}
